import { useQuery, UseQueryResult } from 'react-query';
import { GridSortItem } from '@mui/x-data-grid';
import {
  AxiosGETRequest,
  KeyValuePair,
  PaginatedGETResponse,
  Pagination,
  sendRequest,
  transformPaginationToSearchParams,
  transformSortingToSearchParams
} from '@myme/utils';
import { handleError } from 'src/utils';
import { Filter } from './useFilters';

export interface QueryStringValues {
  pagination: Pagination;
  filter: Filter;
  sorting: GridSortItem;
}

type GridCacheKeys = (string | number)[];

interface Options {
  args?: KeyValuePair[];
  params?: KeyValuePair[];
}

interface UnknownResponse {}

export const useLoadRows = <T>(
  request: AxiosGETRequest,
  queryStringValues: QueryStringValues,
  options?: Options
): UseQueryResult<PaginatedGETResponse<UnknownResponse>> => {
  const { pagination, filter, sorting } = queryStringValues;
  const cacheKeys: GridCacheKeys = [
    ...request.cacheKeys,
    pagination.page,
    pagination.pageSize,
    sorting.sort!,
    Number(filter.toggleReloadData)
  ];

  const rows = useQuery(
    cacheKeys,
    async () => {
      let params: KeyValuePair[] = [
        { key: filter.columnField, value: filter.value },
        ...transformPaginationToSearchParams(pagination),
        ...transformSortingToSearchParams(sorting)
      ];

      if (options?.params) {
        params = params.concat(options?.params);
      }

      const { data } = await sendRequest<T, null>(request, {
        args: options?.args ?? [],
        queryStringParams: params
      });

      return data;
    },
    { onError: (e: unknown) => handleError(e, { navigateToErrorPage: false }), cacheTime: 0, keepPreviousData: true }
  );

  return rows as UseQueryResult<PaginatedGETResponse<T>>;
};
